/**
 * Objective and action commands (P1-1..P1-5, P1-17/P1-18): pure functions
 * that validate a request against the current derived state and, on
 * success, return the event(s) to append. Callers (the DomainApi
 * implementation in `src/data`) are responsible for actually appending
 * through `EventCachePort` and for supplying `today`/`now` from `ClockPort`
 * and IDs from `IdPort` -- this module performs no I/O (ADR-0001 rule 2).
 *
 * Each function returns `{ events }` on success or throws a
 * `DomainErrorInfo`-shaped error (domain-api.ts `DomainErrorCode`) on
 * failure, mirroring how `DomainApi` commands are documented to reject.
 *
 * Max 5 active objectives (R3.5, P1's "explicitly NOT in P1" note says the
 * cap itself is WON'T-for-now per PRD v0.10 WONT list -- see phase-1-mvp.md
 * §1 "Explicitly NOT in P1" and PRD §10 WON'T list: "the max-5-active-
 * objectives cap"). Per that ruling, `createObjective` in Phase 1 does NOT
 * enforce the cap; `domain-api.ts`'s `objective_cap_reached` error code
 * exists in the contract for when Phase 3 turns the cap back on, but is
 * intentionally unreachable in this Phase 1 implementation. This is a
 * deliberate scope decision, not an oversight -- flagged in the build report.
 *
 * Phase 5 additions (ADR-0002 addendum 2026-07-04): `createObjective` honors
 * `draft.initialStatus` (defaults to 'active' when omitted -- the new
 * desktop creation screen is the only caller that passes 'draft' explicitly;
 * every pre-Phase-5 caller, including the existing phone "New objective"
 * flow, is unaffected, R3.9); `setObjectiveStatus` is the new
 * general-purpose state-machine transition command (draft/active/on_hold/
 * archived), additive alongside the unchanged `archiveObjective`;
 * `setActionStatus` (to_do/doing); `linkObjectives`/`unlinkObjectives`
 * (symmetric many-to-many); `updateObjectiveDetails` (category/effort/
 * priority/reminder/cadence goal/visibility toggles); and per-action
 * `effortMinutes` on `addAction`/`editAction`.
 */

import type {
  ActionAddedEvent,
  ActionCompletedEvent,
  ActionEditedEvent,
  ActionId,
  ActionRemovedEvent,
  ActionStatus,
  ActionStatusChangedEvent,
  ActionsReorderedEvent,
  ClockPort,
  CompassEvent,
  IdPort,
  IsoDate,
  LogEntryRevertedEvent,
  ObjectiveArchivedEvent,
  ObjectiveCategory,
  ObjectiveCreatedEvent,
  ObjectiveDraft,
  ObjectiveEditedEvent,
  ObjectiveId,
  ObjectiveLevelOfEffort,
  ObjectiveLinkedEvent,
  ObjectivePinChangedEvent,
  ObjectivePriority,
  ObjectiveReminder,
  ObjectiveStatus,
  ObjectiveStatusChangedEvent,
  ObjectiveTargetDateAdjustedEvent,
  ObjectiveUnlinkedEvent,
  WeekStart,
} from '../../contracts';
import { type CommandResult, domainError } from '../event-log/command-error';
import { addDaysToDate, weekOf } from '../habits/week';
import { buildObjectiveStates, resolveLinkedObjectiveIds } from './state';

/**
 * The backfill window (ADR-0006, 2026-07-03 addendum): today, or any date
 * within the current Mon-Sun week or the immediately preceding Mon-Sun week.
 * Floor = the Monday of LAST week (one week before the Monday of the current
 * week); ceiling = today itself (a future date is never valid regardless of
 * week membership -- there is no "backfilling tomorrow"). Built entirely from
 * the existing `weekOf`/`addDaysToDate` primitives per the addendum's own
 * guidance -- no new date-math primitive.
 *
 * Lifecycle commands (create/edit/archive/target-adjust) do NOT call this
 * gate at all -- confirmed by the same addendum via direct reading of every
 * command in this module and `habits/commands.ts`; they stamp
 * `occurredOn: ports.clock.today()` directly and are therefore unaffected by
 * this window widening (AC-P13/AC-P16), verified by regression test rather
 * than by a code change here.
 */
export function assertValidRecordingDate(
  requested: IsoDate,
  clock: ClockPort,
): void {
  const today = clock.today();
  const currentWeekStart = weekOf(today);
  const floor = addDaysToDate(currentWeekStart, -7) as IsoDate;
  if (requested < floor || requested > today) {
    throw domainError(
      'invalid_date',
      `date must be within the current week and the preceding week (${floor}..${today}), got ${requested}`,
    );
  }
}

export function createObjective(
  // Unused in Phase 1 by design: the 5-active-objectives cap is WON'T-for-now
  // (see module doc comment above); the log is not consulted here. Kept as a
  // parameter for signature symmetry with every other command in this module
  // and so Phase 3 can wire the cap check back in without an API change.
  _events: readonly CompassEvent[],
  draft: ObjectiveDraft,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ObjectiveCreatedEvent> {
  if (draft.title.trim().length === 0) {
    throw domainError('invalid_input', 'objective title must not be empty');
  }

  // Mirrors addAction's own title check: without this, bundling actions
  // into initial objective creation (draft.actions) was the one path that
  // let an empty/whitespace-only action title reach the domain -- addAction
  // itself already rejects it, but createObjective's action bundle skipped
  // the check entirely and passed every title straight into the event.
  for (const action of draft.actions) {
    if (action.title.trim().length === 0) {
      throw domainError('invalid_input', 'action title must not be empty');
    }
  }

  const objectiveId = ports.id.nextRawId() as ObjectiveId;
  const today = ports.clock.today();

  const event: ObjectiveCreatedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'objective.created',
    schemaVersion: 1,
    occurredOn: today,
    recordedAt: ports.clock.now(),
    objectiveId,
    title: draft.title,
    why: draft.why,
    targetDate: draft.targetDate,
    actions: draft.actions.map((action) => ({
      actionId: ports.id.nextRawId() as ActionId,
      title: action.title,
      dueDate: action.dueDate,
      milestoneTag: action.milestoneTag,
      effortMinutes: action.effortMinutes ?? null,
    })),
    // Phase 5 (R3.9, ADR-0002 addendum 2026-07-04 D1): defaults to 'active'
    // when the caller omits initialStatus -- the load-bearing
    // backward-compatibility rule that keeps the existing phone "New
    // objective" flow (NewObjectiveScreen.tsx, which never sets this field)
    // completely unaffected: those objectives are immediately real for
    // Today-surfacing/forecast, exactly as before this phase. ONLY the new
    // desktop creation/editing screen passes initialStatus: 'draft'
    // explicitly, matching its "Commit objective" framing (creation and
    // commitment are separate acts on that screen only) -- see
    // ObjectiveDraft.initialStatus's own doc comment (domain-api.ts).
    initialStatus: draft.initialStatus ?? 'active',
  };

  return { events: [event] };
}

export function editObjective(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  changes: { readonly title?: string; readonly why?: string },
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ObjectiveEditedEvent> {
  requireObjective(events, objectiveId);

  const event: ObjectiveEditedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'objective.edited',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
    changes,
  };

  return { events: [event] };
}

/**
 * The category/effort/priority/reminder/cadence-goal/visibility-toggle edit
 * surface (R3.8/R3.12/R3.14/R3.15, Phase 5) -- kept as its own command,
 * mirroring why `adjustObjectiveTargetDateManually` is separate from
 * `editObjective`: a distinct field set with its own call site
 * (`DomainApi.updateObjectiveDetails`), all riding on the same
 * `objective.edited` event type under the hood (ADR-0002 addendum
 * 2026-07-04). No validation beyond "objective exists" -- every field here
 * is optional/nullable with no domain invariant to violate (unlike, say,
 * `targetPerWeek < 1` for habits).
 */
export function updateObjectiveDetails(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  changes: {
    readonly category?: ObjectiveCategory | null;
    readonly levelOfEffort?: ObjectiveLevelOfEffort | null;
    readonly priority?: ObjectivePriority | null;
    readonly reminder?: ObjectiveReminder | null;
    readonly cadenceGoalPerWeek?: number | null;
    readonly includeInWeeklyReview?: boolean;
    readonly surfaceOnToday?: boolean;
    readonly ambientRewardOnComplete?: boolean;
  },
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ObjectiveEditedEvent> {
  requireObjective(events, objectiveId);

  const event: ObjectiveEditedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'objective.edited',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
    changes,
  };

  return { events: [event] };
}

export function adjustObjectiveTargetDateManually(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  nextTargetDate: IsoDate,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ObjectiveTargetDateAdjustedEvent> {
  const state = requireObjective(events, objectiveId);

  const event: ObjectiveTargetDateAdjustedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'objective.target_date_adjusted',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
    previousTargetDate: state.targetDate as IsoDate,
    nextTargetDate,
    source: 'manual',
    reviewWeek: null,
  };

  return { events: [event] };
}

/**
 * The one-click accept for a slipping objective (P1-18, AC-7): records
 * `objective.target_date_adjusted` with `source: 'forecast_suggestion'` --
 * distinct from a plain completion event and from a manual edit, so Phase
 * 2's reward engine can recognize it as realism-bonus-eligible without a
 * schema change (exactly P1-18's requirement). No XP is awarded here --
 * there is no reward engine in Phase 1 (CLAUDE.md scope boundary); the
 * event itself is the deliverable.
 *
 * BUG FIX (found via a UI regression test, `ObjectiveDetailScreen.test.tsx`'s
 * "records reviewWeek as the LOCAL Monday" case): `reviewWeek` used to be
 * hardcoded `null` here regardless of what the caller supplied, silently
 * dropping the value every UI call site (`ObjectiveDetailScreen.tsx`,
 * `ObjectiveFormScreen.desktop.tsx`) already computed and passed through
 * `DomainApi.acceptObjectiveDateSuggestion`'s `ObjectiveDateSuggestionView.
 * reviewWeek` field (a BINDING contract field, `domain-api.ts`). The
 * `objective.target_date_adjusted` event's own doc comment
 * (`contracts/events.ts`) says `reviewWeek` is "null for manual edits" --
 * implying non-null for a forecast-suggestion accept -- and the habit-side
 * counterpart (`review/commands.ts`'s `acceptSuggestion`) already threads
 * `suggestion.reviewWeek` through correctly; this command just never
 * accepted the parameter to do the same. `reviewWeek` is caller-supplied
 * (not recomputed here) for the same TOCTOU reason `acceptSuggestion`
 * documents: accepting exactly what the UI showed avoids a mismatch between
 * what the user saw and what gets recorded.
 */
export function acceptObjectiveDateSuggestion(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  suggestedTargetDate: IsoDate,
  reviewWeek: WeekStart | null,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ObjectiveTargetDateAdjustedEvent> {
  const state = requireObjective(events, objectiveId);

  const event: ObjectiveTargetDateAdjustedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'objective.target_date_adjusted',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
    previousTargetDate: state.targetDate as IsoDate,
    nextTargetDate: suggestedTargetDate,
    source: 'forecast_suggestion',
    reviewWeek,
  };

  return { events: [event] };
}

/**
 * Unchanged from pre-Phase-5 (ADR-0002 addendum 2026-07-04 D1): still
 * produces a plain `objective.archived` event, still the command every
 * pre-existing archive-from-anywhere UI flow calls. `setObjectiveStatus`
 * below is the new, additional, more general surface -- both are
 * permanently valid producers of the archived state, resolved together by
 * `resolveEffectiveState` (`objectives/state.ts`).
 */
export function archiveObjective(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ObjectiveArchivedEvent> {
  requireObjective(events, objectiveId);

  const event: ObjectiveArchivedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'objective.archived',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
  };

  return { events: [event] };
}

/**
 * Valid `(from, to)` status transitions (R3.9, Phase 5, ADR-0002 addendum
 * 2026-07-04 D1). `draft -> on_hold`, `on_hold -> draft`, and anything FROM
 * `archived` are all invalid -- un-archiving is `revertLogEntry`-only, never
 * a forward `status_changed`.
 */
const VALID_STATUS_TRANSITIONS: ReadonlySet<string> = new Set([
  'draft:active', // "Commit objective"
  'draft:archived', // discard a draft
  'active:on_hold',
  'active:archived',
  'on_hold:active', // resume
  'on_hold:archived',
]);

/**
 * The full state-machine transition command (R3.9, Phase 5, ADR-0002
 * addendum 2026-07-04 D1): "Commit objective" (draft->active), the Status
 * segmented control (active<->on_hold), and discard/archive from either
 * (draft/active/on_hold->archived). Rejects invalid transitions (including
 * a no-op "change" to the same status) with `invalid_input` -- this mirrors
 * how `requireObjective` already rejects mutating an archived objective,
 * just generalized to the full transition table rather than a single
 * boolean.
 */
export function setObjectiveStatus(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  nextStatus: ObjectiveStatus,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ObjectiveStatusChangedEvent> {
  const states = buildObjectiveStates(events);
  const state = states.get(objectiveId);
  if (!state) {
    throw domainError('objective_not_found', `objective ${objectiveId} not found`);
  }

  const previousStatus = state.status;
  const transitionKey = `${previousStatus}:${nextStatus}`;
  if (!VALID_STATUS_TRANSITIONS.has(transitionKey)) {
    throw domainError(
      'invalid_input',
      `cannot transition objective status from ${previousStatus} to ${nextStatus}`,
    );
  }

  const event: ObjectiveStatusChangedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'objective.status_changed',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
    previousStatus,
    nextStatus,
  };

  return { events: [event] };
}

export function addAction(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  input: {
    readonly title: string;
    readonly dueDate: IsoDate | null;
    readonly milestoneTag: string | null;
    readonly position: number | null;
    readonly effortMinutes?: number | null;
  },
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ActionAddedEvent> {
  requireObjective(events, objectiveId);

  if (input.title.trim().length === 0) {
    throw domainError('invalid_input', 'action title must not be empty');
  }

  const event: ActionAddedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'action.added',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
    actionId: ports.id.nextRawId() as ActionId,
    title: input.title,
    dueDate: input.dueDate,
    milestoneTag: input.milestoneTag,
    position: input.position,
    effortMinutes: input.effortMinutes ?? null,
  };

  return { events: [event] };
}

export function editAction(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  actionId: ActionId,
  changes: {
    readonly title?: string;
    readonly dueDate?: IsoDate | null;
    readonly milestoneTag?: string | null;
    readonly effortMinutes?: number | null;
  },
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ActionEditedEvent> {
  requireAction(events, objectiveId, actionId);

  const event: ActionEditedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'action.edited',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
    actionId,
    changes,
  };

  return { events: [event] };
}

export function removeAction(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  actionId: ActionId,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ActionRemovedEvent> {
  requireAction(events, objectiveId, actionId);

  const event: ActionRemovedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'action.removed',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
    actionId,
  };

  return { events: [event] };
}

/**
 * One tap (P1-5); undo goes through `revertLogEntry` below. `date` is
 * caller-supplied and gated by `assertValidRecordingDate` at the DomainApi
 * layer (phase-3-plan.md BF-2) -- this function itself just stamps
 * `occurredOn: date`, mirroring `completeHabit`/`logClean`/`logLapse` in
 * `habits/commands.ts`, which take the same shape for the same reason: a
 * viewed/backfilled date must never silently fall back to `clock.today()`.
 */
export function completeAction(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  actionId: ActionId,
  date: IsoDate,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ActionCompletedEvent> {
  requireAction(events, objectiveId, actionId);

  const event: ActionCompletedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'action.completed',
    schemaVersion: 1,
    occurredOn: date,
    recordedAt: ports.clock.now(),
    objectiveId,
    actionId,
  };

  return { events: [event] };
}

/**
 * The `to_do`/`doing` toggle (R3.10, Phase 5, ADR-0002 addendum 2026-07-04
 * D3) -- math-inert, one behavioral effect (next-up ordering preference).
 * No validation beyond "action exists" -- setting `'doing'` on an
 * already-`done` action is not rejected here (completion always wins
 * regardless, per `computeActionDisplayStatus`), matching the contract's own
 * "not itself an error" note.
 */
export function setActionStatus(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  actionId: ActionId,
  status: ActionStatus,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ActionStatusChangedEvent> {
  requireAction(events, objectiveId, actionId);

  const event: ActionStatusChangedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'action.status_changed',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
    actionId,
    status,
  };

  return { events: [event] };
}

export function reorderActions(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  orderedActionIds: readonly ActionId[],
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ActionsReorderedEvent> {
  requireObjective(events, objectiveId);

  const event: ActionsReorderedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'actions.reordered',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
    orderedActionIds,
  };

  return { events: [event] };
}

/**
 * Normalize an unordered objective pair to a canonical (lexicographically
 * smaller ID first) order, so link/unlink resolution is deterministic
 * regardless of which objective the user acted from (ADR-0002 addendum
 * 2026-07-04 D4).
 */
function normalizePair(
  objectiveIdA: ObjectiveId,
  objectiveIdB: ObjectiveId,
): readonly [ObjectiveId, ObjectiveId] {
  return objectiveIdA < objectiveIdB
    ? [objectiveIdA, objectiveIdB]
    : [objectiveIdB, objectiveIdA];
}

/**
 * Symmetric objective-to-objective linking (R3.13, Phase 5, ADR-0002
 * addendum 2026-07-04 D4) -- purely informational, no rollup math. Rejects
 * linking an objective to itself (`invalid_input`) and requires both
 * objectives to exist. Re-linking an already-linked pair is not rejected
 * (parity resolution handles it naturally, per the addendum) -- this
 * function does not need to check current link state before appending.
 */
export function linkObjectives(
  events: readonly CompassEvent[],
  objectiveIdA: ObjectiveId,
  objectiveIdB: ObjectiveId,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ObjectiveLinkedEvent> {
  if (objectiveIdA === objectiveIdB) {
    throw domainError('invalid_input', 'cannot link an objective to itself');
  }
  requireObjective(events, objectiveIdA);
  requireObjective(events, objectiveIdB);

  const [a, b] = normalizePair(objectiveIdA, objectiveIdB);
  const event: ObjectiveLinkedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'objective.linked',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveIdA: a,
    objectiveIdB: b,
  };

  return { events: [event] };
}

export function unlinkObjectives(
  events: readonly CompassEvent[],
  objectiveIdA: ObjectiveId,
  objectiveIdB: ObjectiveId,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ObjectiveUnlinkedEvent> {
  if (objectiveIdA === objectiveIdB) {
    throw domainError('invalid_input', 'cannot unlink an objective from itself');
  }
  requireObjective(events, objectiveIdA);
  requireObjective(events, objectiveIdB);

  const [a, b] = normalizePair(objectiveIdA, objectiveIdB);
  const event: ObjectiveUnlinkedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'objective.unlinked',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveIdA: a,
    objectiveIdB: b,
  };

  return { events: [event] };
}

/**
 * Pin/unpin plus reorder (R8.8, Phase 6, ADR-0002 addendum 2026-07-07
 * D-PinnedObjectives) -- `pinnedOrder` is normalized to `null` whenever
 * `pinned` is false regardless of what the caller passes (the event's own
 * doc comment: "an unpinned objective has no order to carry"), so a caller
 * never needs to remember to clear it itself when unpinning. No effect on
 * progress/forecast/XP/the 5-active cap (AC-T19) -- this command touches
 * only the pin/order fields, nothing else in `ObjectiveState`.
 */
export function setObjectivePinned(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  pinned: boolean,
  pinnedOrder: number | null,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<ObjectivePinChangedEvent> {
  requireObjective(events, objectiveId);

  const event: ObjectivePinChangedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'objective.pin_changed',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    objectiveId,
    pinned,
    pinnedOrder: pinned ? pinnedOrder : null,
  };

  return { events: [event] };
}

/**
 * Generic revert (ADR-0002): valid targets include `action.completed`. This
 * function only constructs the event -- it does not itself validate that
 * `targetEventId` refers to a currently-active, revertible event; the
 * DomainApi layer is expected to look up the target event's type from the
 * full log and reject with `not_revertible` if it is not one of the
 * documented valid target types before calling this.
 */
export function revertLogEntry(
  targetEventId: string,
  ports: { readonly clock: ClockPort; readonly id: IdPort },
): CommandResult<LogEntryRevertedEvent> {
  const event: LogEntryRevertedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'log_entry.reverted',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    revertedEventId: targetEventId as LogEntryRevertedEvent['revertedEventId'],
  };

  return { events: [event] };
}

/**
 * Unchanged behavior for Phase-1-era callers: rejects an unknown OR archived
 * objective. Phase 5 (ADR-0002 addendum 2026-07-04): does NOT additionally
 * reject `draft`/`on_hold` objectives here -- editing/adding actions to a
 * draft (still being composed) or an on-hold objective (paused, not gone)
 * are both legitimate operations; only `archived` is terminal. Today-
 * surfacing/forecast/weekly-review/XP gating for draft/on_hold is handled
 * by their own consumers (`today/due-actions.ts`, `review/weekly-review.ts`,
 * `rewards/xp.ts`), not by blocking mutation here.
 */
function requireObjective(events: readonly CompassEvent[], objectiveId: ObjectiveId) {
  const states = buildObjectiveStates(events);
  const state = states.get(objectiveId);
  if (!state || state.status === 'archived') {
    throw domainError('objective_not_found', `objective ${objectiveId} not found`);
  }
  return state;
}

function requireAction(
  events: readonly CompassEvent[],
  objectiveId: ObjectiveId,
  actionId: ActionId,
) {
  const state = requireObjective(events, objectiveId);
  const action = state.actionsById.get(actionId);
  if (!action || action.removed) {
    throw domainError('action_not_found', `action ${actionId} not found`);
  }
  return action;
}

// Re-exported for callers that need to inspect linked-objective state
// without a full ObjectiveView projection (e.g. the DomainApi layer
// confirming a link exists before appending an unlink, if it chooses to).
export { resolveLinkedObjectiveIds };
